/* Blog product embed — hydrates product links in article content into
   full-width carousel cards. Progressive enhancement over the markup the
   blog pipeline already emits: an <h2>/<h3> product link followed by a
   static <figure><img>. The static figure is replaced by the widget; when
   the product fetch fails (deleted/draft product) the article is left
   exactly as authored. Paragraph product links are never touched. */
(() => {
  'use strict';

  const REF_PARAM = 'ref=blog-embed';
  const MAX_SLIDES = 6;
  const productCache = {};

  const rootUrl = () =>
    (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.rootUrlWithoutSlash) || '';

  function loadProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch(`${rootUrl()}/products/${handle}.js`).then((res) => {
        if (!res.ok) throw new Error(`product ${handle}: ${res.status}`);
        return res.json();
      });
    }
    return productCache[handle];
  }

  /* Prefer the theme's own formatter (custom.js formatShopMoney covers every
     Shopify placeholder variant); the local fallback handles the common four. */
  function formatMoney(cents) {
    if (typeof window.formatShopMoney === 'function') return window.formatShopMoney(cents);
    const format =
      (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) ||
      '${{amount}}';
    const value = cents / 100;
    const replacements = {
      amount: value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      amount_no_decimals: Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      amount_with_comma_separator: value.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      amount_no_decimals_with_comma_separator: Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => replacements[key] ?? replacements.amount);
  }

  function productHandleFromHref(href) {
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/\/products\/([a-z0-9][a-z0-9-]*)/i);
      return match ? match[1].toLowerCase() : null;
    } catch {
      return null;
    }
  }

  function imageUrl(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return src;
    }
  }

  function isImageBlock(node) {
    /* Articles not re-synced since the image normalizer shipped still have
       bare <img> as direct children of .rte — 11 live articles at last audit. */
    if (node.tagName === 'IMG') return true;
    if (node.tagName === 'FIGURE') return Boolean(node.querySelector('img'));
    if (node.tagName === 'P') {
      const imgs = node.querySelectorAll('img');
      return imgs.length > 0 && imgs.length === node.children.length && node.textContent.trim() === '';
    }
    return false;
  }

  const hasProductLink = (el) => Boolean(el.querySelector('a[href*="/products/"]'));

  /* First <figure> (or image-only <p>) between the heading and the next
     h2/h3 — the static product image the pipeline inserted. */
  function findStaticImageBlock(heading) {
    let node = heading.nextElementSibling;
    while (node && !/^H[23]$/.test(node.tagName)) {
      if (isImageBlock(node)) return node;
      node = node.nextElementSibling;
    }
    return null;
  }

  /* First image block after a product-linking paragraph, stopping at the next
     heading or the next paragraph that links a product (that one claims its
     own figure). Used for the article format where the heading is plain text
     and the product link lives in the description paragraph under it. */
  function findImageBlockAfterParagraph(paragraph) {
    let node = paragraph.nextElementSibling;
    while (node && !/^H[23]$/.test(node.tagName)) {
      if (isImageBlock(node)) return node;
      if (node.tagName === 'P' && hasProductLink(node)) return null;
      node = node.nextElementSibling;
    }
    return null;
  }

  /* Image block sandwiched between the section heading and the linking
     paragraph (legacy listicle format: h2 → figure → description). Preferred
     over the forward search because an image in that slot is the product
     image "under the title" by the pipeline's own convention. */
  function findImageBlockBetween(heading, paragraph, claimed) {
    let node = heading.nextElementSibling;
    while (node && node !== paragraph && !/^H[23]$/.test(node.tagName)) {
      if (isImageBlock(node) && !claimed.has(node)) return node;
      node = node.nextElementSibling;
    }
    return null;
  }

  function previousHeading(el) {
    let node = el.previousElementSibling;
    while (node && !/^H[23]$/.test(node.tagName)) node = node.previousElementSibling;
    return node;
  }

  function slideAspect(product) {
    const media = (product.media || []).find((m) => m.preview_image);
    const ratio = media && media.preview_image.height ? media.preview_image.width / media.preview_image.height : 1.5;
    /* Square specimen art renders uncropped (a static square figure is just as
       tall today); only portraits are cropped up to square and panoramas
       clamped so they don't become a thin strip. */
    return Math.min(Math.max(ratio, 1), 1.9);
  }

  function buildWidget(product) {
    const productUrl = `${rootUrl()}/products/${product.handle}?${REF_PARAM}`;
    const images = (product.images || []).slice(0, MAX_SLIDES);
    if (images.length === 0) return null;

    const root = document.createElement('aside');
    root.className = 'bpe';
    root.style.setProperty('--bpe-aspect', String(slideAspect(product)));
    root.setAttribute('aria-label', product.title);

    const carousel = document.createElement('div');
    carousel.className = 'bpe__carousel';

    const viewport = document.createElement('div');
    viewport.className = 'bpe__viewport';
    viewport.setAttribute('tabindex', '0');
    viewport.setAttribute('role', 'group');
    viewport.setAttribute('aria-label', `${product.title} images`);

    images.forEach((src, index) => {
      const slide = document.createElement('a');
      slide.className = 'bpe__slide';
      slide.href = productUrl;
      slide.setAttribute('data-instant', '');
      const img = document.createElement('img');
      img.src = imageUrl(src, 1400);
      img.alt = `${product.title} — image ${index + 1}`;
      img.loading = index === 0 ? 'eager' : 'lazy';
      img.decoding = 'async';
      slide.appendChild(img);
      viewport.appendChild(slide);
    });
    carousel.appendChild(viewport);

    let dots = [];
    if (images.length > 1) {
      const makeArrow = (dir) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `bpe__arrow bpe__arrow--${dir}`;
        btn.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
        btn.innerHTML =
          dir === 'prev'
            ? '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            : '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        btn.addEventListener('click', () => {
          const target = viewport.scrollLeft + (dir === 'prev' ? -viewport.clientWidth : viewport.clientWidth);
          const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          viewport.scrollTo({ left: target, behavior: smooth ? 'smooth' : 'auto' });
        });
        return btn;
      };
      carousel.appendChild(makeArrow('prev'));
      carousel.appendChild(makeArrow('next'));

      const dotsWrap = document.createElement('div');
      dotsWrap.className = 'bpe__dots';
      dots = images.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bpe__dot';
        dot.setAttribute('aria-label', `Go to image ${index + 1}`);
        dot.addEventListener('click', () => {
          const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          viewport.scrollTo({ left: index * viewport.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
        });
        dotsWrap.appendChild(dot);
        return dot;
      });
      carousel.appendChild(dotsWrap);

      const syncDots = () => {
        const index = Math.min(dots.length - 1, Math.round(viewport.scrollLeft / Math.max(1, viewport.clientWidth)));
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      };
      viewport.addEventListener('scroll', () => window.requestAnimationFrame(syncDots), { passive: true });
      syncDots();
    }
    root.appendChild(carousel);

    const meta = document.createElement('div');
    meta.className = 'bpe__meta';

    const info = document.createElement('div');
    info.className = 'bpe__info';
    const titleLink = document.createElement('a');
    titleLink.className = 'bpe__title';
    titleLink.href = productUrl;
    titleLink.setAttribute('data-instant', '');
    titleLink.textContent = product.title;
    info.appendChild(titleLink);
    if (product.vendor) {
      const vendor = document.createElement('span');
      vendor.className = 'bpe__vendor';
      vendor.textContent = `by ${product.vendor}`;
      info.appendChild(vendor);
    }
    meta.appendChild(info);

    const actions = document.createElement('div');
    actions.className = 'bpe__actions';
    const price = document.createElement('span');
    price.className = 'bpe__price';
    if (product.price === 0 && !product.price_varies) {
      price.textContent = 'Free';
      price.classList.add('bpe__price--free');
    } else {
      price.textContent = product.price_varies ? `From ${formatMoney(product.price)}` : formatMoney(product.price);
      if (product.compare_at_price && product.compare_at_price > product.price) {
        const compare = document.createElement('s');
        compare.className = 'bpe__compare';
        compare.textContent = formatMoney(product.compare_at_price);
        price.appendChild(document.createTextNode(' '));
        price.appendChild(compare);
      }
    }
    actions.appendChild(price);
    const cta = document.createElement('a');
    cta.className = 'bpe__cta';
    cta.href = productUrl;
    cta.setAttribute('data-instant', '');
    cta.textContent = 'View product';
    actions.appendChild(cta);
    meta.appendChild(actions);
    root.appendChild(meta);

    return root;
  }

  /* Two article formats are hydrated, targets resolved synchronously up front
     so async fetches can't double-claim a figure:
     1. The heading IS the product link (weekly-drops/older listicles): the
        figure between it and the next heading becomes the widget; with no
        figure the widget is inserted right after the heading.
     2. The heading is plain text and the product link sits in the paragraph
        under it (newer listicles/essays): that paragraph's next figure
        becomes the widget. No figure = no widget, so prose-only mentions
        stay untouched. Paragraph links themselves are never altered. */
  function collectJobs(rte) {
    const jobs = [];
    const claimed = new Set();

    rte.querySelectorAll('h2, h3').forEach((heading) => {
      const link = heading.querySelector('a[href*="/products/"]');
      const handle = link && productHandleFromHref(link.getAttribute('href'));
      if (!handle) return;
      const target = findStaticImageBlock(heading);
      if (target) claimed.add(target);
      jobs.push({ handle, target, heading });
    });

    rte.querySelectorAll('p').forEach((paragraph) => {
      const link = paragraph.querySelector('a[href*="/products/"]');
      if (!link) return;
      const heading = previousHeading(paragraph);
      if (!heading) return; /* intro prose must never claim the lead image */
      if (hasProductLink(heading)) return; /* format 1 owns this section */
      const handle = productHandleFromHref(link.getAttribute('href'));
      if (!handle) return;
      const target =
        findImageBlockBetween(heading, paragraph, claimed) ||
        findImageBlockAfterParagraph(paragraph);
      if (!target || claimed.has(target)) return;
      claimed.add(target);
      jobs.push({ handle, target, heading: null });
    });

    return jobs;
  }

  async function runJob(job) {
    let product;
    try {
      product = await loadProduct(job.handle);
    } catch {
      return; /* deleted or unpublished product: leave the article as authored */
    }
    const widget = buildWidget(product);
    if (!widget) return;

    if (job.target) job.target.replaceWith(widget);
    else if (job.heading) job.heading.insertAdjacentElement('afterend', widget);
  }

  function init() {
    const rte = document.querySelector('.shopify-section--main-article .article__content .rte');
    if (!rte) return;
    collectJobs(rte).forEach(runJob);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
